import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';

import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { ParticipanteAta, SituacaoPresencaAta } from '@/features/comissoes/dashboard-comissoes/comissoes-dashboard.model';
import { PessoaAvatarComponent } from '@/shared/components/pessoa/pessoa-avatar/pessoa-avatar';
import { TipoParticipanteAta, classeSituacaoAta, iconeSituacaoAta, rotuloPresencaAta } from '../ata-reuniao.presenter';

/**
 * Tabela de presença de participantes da ata — reutilizada por membros e convidados. Apenas
 * apresentação: cada linha traz suas ações (inserir na ata / alterar presença / remover) sempre
 * visíveis numa coluna "Ações"; o componente pai (`AtaReuniaoComponent`) trata cada ação.
 */
@Component({
    selector: 'app-ata-tabela-participantes',
    standalone: true,
    imports: [CommonModule, ButtonModule, TableModule, CanDirective, PessoaAvatarComponent],
    templateUrl: './ata-tabela-participantes.html',
    styleUrls: ['./ata-tabela-participantes.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AtaTabelaParticipantesComponent {
    /** Participantes exibidos (membros ou convidados). */
    readonly participantes = input<ParticipanteAta[]>([]);
    /** Rótulo da coluna de nome ("Membro"/"Convidado"). */
    readonly rotuloColuna = input('');
    /** Papel dos participantes (M = membro, C = convidado). */
    readonly tipo = input.required<TipoParticipanteAta>();
    /** Habilita as ações (status da ata permite alteração). */
    readonly permiteAlteracao = input(false);
    /** Altura de rolagem da tabela. */
    readonly scrollHeight = input('14rem');
    /** Exibe o botão "Remover" na coluna de ações (usado por convidados). */
    readonly permiteRemover = input(false);
    /** Exibe a coluna de avatar (foto) antes da UF. */
    readonly mostrarAvatar = input(false);
    /** Tamanho do avatar exibido na coluna. */
    readonly tamanhoAvatar = input<'sm' | 'md' | 'lg' | 'xl'>('sm');
    /** Renderiza sem o card externo (flat), para uso dentro de um `app-card-content`. */
    readonly plano = input(false);

    /** Inserir o participante da linha no texto da ata. */
    readonly inserirNaAta = output<ParticipanteAta>();
    /** Abrir o diálogo de alteração de presença do participante da linha. */
    readonly alterarPresenca = output<ParticipanteAta>();
    /** Remover um convidado (com o evento para interromper a propagação). */
    readonly remover = output<{ participante: ParticipanteAta; event: Event }>();

    protected readonly roleGerenciarConvidados = PERMISSOES.comissoes.atas.gerenciarConvidados;

    protected icone(situacao: SituacaoPresencaAta): string {
        return iconeSituacaoAta(situacao);
    }

    protected classe(situacao: SituacaoPresencaAta): string {
        return classeSituacaoAta(situacao);
    }

    protected rotulo(participante: ParticipanteAta): string {
        return rotuloPresencaAta(participante, this.tipo());
    }
}
